import _ from 'underscore';
import React, { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ChoiceGroup, Label, getId, Shimmer, ShimmerElementType, Button } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { BingMapsSearchBox } from './bing-maps-search-box';
import { getSearchOptionsStyles } from './get-styles';

export const SEARCH_TYPE = {
  Quick: 'Quick',
  Boundary: 'Boundary',
  Radius: 'Radius',
  Mixed: 'Mixed',
};

function withSearchOptions(WrappedComponent) {
  const HOC = (props) => {
    const {
      allowQuickLocationTargeting,
      allowNegativeLocationTargeting,
      allowPositiveLocationTargeting,
      allowBoundaryLocationSearch,
      isMixedLocationSearch,
      onQuickTargetingSelected,
      isRadiusExcludeEnabled,
      className,
      getIsSearchResultBoundaryTarget,
      getIsSearchResultRadiusTarget,
      strings,
      searchResultsFooter,
      isLoading,
      defaultSelectedSearchType,
      showSearchTypeOptions,
      helpPopups,
      userRegion,
      locale,
      showExcludeButtonIgnoreLocationId,
      onBulkLocationButtonClick,
    } = props;

    const classes = useDefaultStyles(getSearchOptionsStyles);

    // Use getId() to ensure that the ID is unique on the page.
    const searchBoxId = getId('bing-maps-search-box');

    const i18n = useContext(I18nContext);
    const [searchType, setSearchType] = useState(defaultSelectedSearchType);

    const quickOptionText = useMemo(() => (strings.quickOption ?
      strings.quickOption : i18n.getString(_TL_('Quick choice'))), [i18n, strings]);

    const boundaryOptionText = useMemo(() => (strings.boundaryOption ?
      strings.boundaryOption : i18n.getString(_TL_('Specific location'))), [i18n, strings]);

    const radiusOptionText = useMemo(() => (strings.radiusOption ?
      strings.radiusOption : i18n.getString(_TL_('Radius'))), [i18n, strings]);

    const mixedOptionText = useMemo(() => (strings.mixedOption ?
      strings.radiusOption : i18n.getString(_TL_('Let me choose specific locations'))), [i18n, strings]);

    const onRenderField = useCallback((optionProps, render) => {
      const { helpPopupId, helpPopupAriaLabel, key } = optionProps;

      if (!helpPopupId) {
        return render(optionProps);
      }

      return (
        <Fragment>
          {render(optionProps)}
          <span className={classNames(`${key}-help-popup`, 'inline-help-popup', classes.inlineHelp)}>
            <HelpPopup
              topic={helpPopupId}
              aria-label={helpPopupAriaLabel}
            />
          </span>
        </Fragment>
      );
    }, [classes.inlineHelp]);

    const choiceGroupOptions = useMemo(() => _.compact([
      (allowQuickLocationTargeting) && {
        key: SEARCH_TYPE.Quick,
        'data-automation-id': 'quick-location-choice',
        text: quickOptionText,
        helpPopupId: helpPopups.quickOptionId,
        helpPopupAriaLabel: helpPopups.quickOptionAriaLabel,
        onRenderField,
      },
      !isMixedLocationSearch && allowBoundaryLocationSearch &&
      {
        key: SEARCH_TYPE.Boundary,
        'data-automation-id': 'boundary-location-choice',
        text: boundaryOptionText,
        helpPopupId: helpPopups.boundaryOptionId,
        helpPopupAriaLabel: helpPopups.boundaryOptionAriaLabel,
        onRenderField,
      },
      !isMixedLocationSearch && (isRadiusExcludeEnabled || allowPositiveLocationTargeting) && {
        key: SEARCH_TYPE.Radius,
        'data-automation-id': 'radius-location-choice',
        text: radiusOptionText,
        helpPopupId: helpPopups.radiusOptionId,
        helpPopupAriaLabel: helpPopups.radiusOptionAriaLabel,
        onRenderField,
      },
      isMixedLocationSearch && {
        key: SEARCH_TYPE.Mixed,
        'data-automation-id': 'mixed-location-choice',
        text: mixedOptionText,
        helpPopupId: helpPopups.mixedOptionId,
        helpPopupAriaLabel: helpPopups.mixedOptionAriaLabel,
        onRenderField,
      },
    ]), [allowQuickLocationTargeting, quickOptionText, isMixedLocationSearch, allowBoundaryLocationSearch,
      boundaryOptionText, isRadiusExcludeEnabled, allowPositiveLocationTargeting, radiusOptionText, mixedOptionText, helpPopups, onRenderField]);

    const onLocationTargetSearchChoiceChange = useCallback((e, option) => {
      setSearchType(option.key);
      onQuickTargetingSelected(option.key === SEARCH_TYPE.Quick);
    }, [onQuickTargetingSelected]);

    const getIsSearchResultBoundaryTargetInternal = useCallback((location, targetingType) => {
      if (searchType === SEARCH_TYPE.Radius) {
        return false;
      }
      return getIsSearchResultBoundaryTarget(location, targetingType);
    }, [getIsSearchResultBoundaryTarget, searchType]);

    const getIsSearchResultRadiusTargetInternal = useCallback((location, targetingType) => {
      if (searchType === SEARCH_TYPE.Boundary) {
        return false;
      }
      return getIsSearchResultRadiusTarget(location, targetingType);
    }, [getIsSearchResultRadiusTarget, searchType]);

    const label = useMemo(() => (strings.label ? strings.label : i18n.getString(_TL_('Search for location'))), [i18n, strings]);

    const placeholder = useMemo(() => {
      if (strings.placeholder) {
        return strings.placeholder;
      }

      if (allowNegativeLocationTargeting && allowPositiveLocationTargeting) {
        return i18n.getString(_TL_('Enter a location to target or exclude'));
      }
      return i18n.getString(_TL_('Enter a location to target'));
    }, [allowPositiveLocationTargeting, allowNegativeLocationTargeting, i18n, strings]);

    const noResultsMessage = useMemo(() => (strings.noResultsMessage ?
      strings.noResultsMessage : i18n.getString(_TL_('Location does not exist or cannot be targeted. Enter a new location.'))), [i18n, strings]);

    const description = useMemo(() => (strings.description ?
      strings.description : i18n.getString(_TL_('For example, a country/region, state/province, coordinates, or ZIP/postal code.'))), [i18n, strings]);

    const componentProps = _.omit(props, 'getIsSearchResultBoundaryTarget', 'getIsSearchResultRadiusTarget');

    const choiceGroupLabel = useMemo(() => (strings.choiceGroupLabel ?
      strings.choiceGroupLabel : i18n.getString(_TL_('I want to add a location target for'))), [i18n, strings]);

    const radiusShimmerElement = [
      { type: ShimmerElementType.line, width: 20, height: 20 },
      { type: ShimmerElementType.gap, width: 5 },
      { type: ShimmerElementType.line, height: 16, width: 175 },
      { type: ShimmerElementType.gap, height: 20, width: '100%' },
    ];

    return (
      isLoading ?
        <div className={classNames(className, 'location-step-shimmer')}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.gap, width: '100%', height: 12 }]} />
          <Shimmer width={175} />
          <Shimmer shimmerElements={[{ type: ShimmerElementType.gap, width: '100%', height: 12 }]} />
          <Shimmer shimmerElements={radiusShimmerElement} />
          <Shimmer shimmerElements={[{ type: ShimmerElementType.gap, width: '100%', height: 12 }]} />
          <Shimmer shimmerElements={radiusShimmerElement} />
          <Shimmer width={134} styles={{ root: { marginBottom: '5px', marginTop: '26px' } }} />
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 380, height: 33 }]} />
          <Shimmer shimmerElements={[{ type: ShimmerElementType.gap, width: '100%', height: 38 }]} />
        </div > :
        <div className={className}>
          {showSearchTypeOptions && <ChoiceGroup
            className="bing-maps-location-targets-search-type-choice-group"
            options={choiceGroupOptions}
            label={choiceGroupLabel}
            selectedKey={searchType}
            onChange={onLocationTargetSearchChoiceChange}
          />}

          {searchType !== SEARCH_TYPE.Quick &&
            <React.Fragment>
              <Label htmlFor={searchBoxId}>{label}</Label>
              <div className={classes.bingMapsSearchBoxAndAddBulkButton}>
                <WrappedComponent
                  {...componentProps}
                  ariaLabel={label}
                  getIsSearchResultBoundaryTarget={getIsSearchResultBoundaryTargetInternal}
                  getIsSearchResultRadiusTarget={getIsSearchResultRadiusTargetInternal}
                  id={searchBoxId}
                  noResultsMessage={noResultsMessage}
                  placeholder={placeholder}
                  searchResultsFooter={searchResultsFooter}
                />
                {searchType !== SEARCH_TYPE.Radius && onBulkLocationButtonClick && onBulkLocationButtonClick !== _.noop && (
                  <Button
                    className="add-in-bulk-button"
                    iconProps={{ iconName: 'AddNotes', style: { color: 'black' } }}
                    onClick={onBulkLocationButtonClick}
                    ariaLabel={i18n.getString(_TL_('Add in bulk button'))}
                  >
                    {i18n.getString(_TL_('Add bulk locations'))}
                  </Button>
                )}
              </div>
              <div className="bing-maps-location-targets-search-prompt">{description}</div>
            </React.Fragment>
          }
        </div>
    );
  };

  HOC.propTypes = {
    allowQuickLocationTargeting: PropTypes.bool,
    allowNegativeLocationTargeting: PropTypes.bool,
    allowPositiveLocationTargeting: PropTypes.bool,
    allowBoundaryLocationSearch: PropTypes.bool,
    isMixedLocationSearch: PropTypes.bool,
    className: PropTypes.string,
    onQuickTargetingSelected: PropTypes.func,
    getIsSearchResultBoundaryTarget: PropTypes.func.isRequired,
    getIsSearchResultRadiusTarget: PropTypes.func.isRequired,
    strings: PropTypes.shape({
      label: PropTypes.string,
      placeholder: PropTypes.string,
      noResultsMessage: PropTypes.string,
      description: PropTypes.string,
      quickOption: PropTypes.string,
      boundaryOption: PropTypes.string,
      radiusOption: PropTypes.string,
      choiceGroupLabel: PropTypes.string,
      mixedOption: PropTypes.string,
    }),
    searchResultsFooter: PropTypes.func,
    isLoading: PropTypes.bool,
    isRadiusExcludeEnabled: PropTypes.bool,
    defaultSelectedSearchType: PropTypes.oneOf(_.values(SEARCH_TYPE)),
    showSearchTypeOptions: PropTypes.bool,
    helpPopups: PropTypes.shape({
      quickOptionId: PropTypes.string,
      quickOptionAriaLabel: PropTypes.string,
      boundaryOptionId: PropTypes.string,
      boundaryOptionAriaLabel: PropTypes.string,
      radiusOptionId: PropTypes.string,
      radiusOptionAriaLabel: PropTypes.string,
      mixedOptionId: PropTypes.string,
      mixedOptionAriaLabel: PropTypes.string,
    }),
    userRegion: PropTypes.string,
    locale: PropTypes.string,
    showExcludeButtonIgnoreLocationId: PropTypes.bool,
    onBulkLocationButtonClick: PropTypes.func,
  };

  HOC.defaultProps = {
    isRadiusExcludeEnabled: true,
    allowQuickLocationTargeting: false,
    allowNegativeLocationTargeting: false,
    allowPositiveLocationTargeting: true,
    allowBoundaryLocationSearch: true,
    isMixedLocationSearch: false,
    className: null,
    onQuickTargetingSelected: _.noop,
    strings: {},
    searchResultsFooter: null,
    isLoading: false,
    defaultSelectedSearchType: SEARCH_TYPE.Boundary,
    showSearchTypeOptions: true,
    helpPopups: {},
    userRegion: undefined,
    locale: undefined,
    showExcludeButtonIgnoreLocationId: false,
    onBulkLocationButtonClick: _.noop,
  };

  HOC.displayName = `withSearchOptions(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
}

export const BingMapsSearchBoxWithSearchOptions = withSearchOptions(BingMapsSearchBox);
